import React from "react";

/* Dashboard — first-scan empty state.

   Shown the very first time a user lands on the dashboard, before any
   scan has run. Every chip, label, and count is sourced from the user's
   saved business profile (toUiProfile in app.jsx) — no demo data:
     - subs/where      → profile.subreddits
     - themes/what     → profile.useCases
     - keywords        → profile.keywords
     - customer/who    → profile.customers
     - painPoints/who  → profile.painPoints (fallback to useCases)
     - profile mark    → profile.initials / profile.color
   The candidates / surfaced / runtime estimate strip scales with the
   user's subreddit count so the numbers stay credible across profiles. */

function DashboardFirstScan({ profile, onNavigate, onScanNow }) {
  const subs = React.useMemo(
    () => (profile?.subreddits || []).map((s) => (s.startsWith("r/") ? s : "r/" + s)),
    [profile]
  );
  const themes = React.useMemo(
    () => (profile?.useCases || []).slice(0, 4),
    [profile]
  );
  const customers = profile?.customers || "—";
  const keywords = profile?.keywords || [];
  // Pain points feel like the "what the customer is trying to do" bullets
  // in the WHO card; if the profile didn't capture any, fall back to
  // useCases so the card never reads empty.
  const whoBullets = React.useMemo(() => {
    const src = (profile?.painPoints || []).length
      ? profile.painPoints
      : (profile?.useCases || []);
    return src.slice(0, 3);
  }, [profile]);

  // Estimate scales with subreddit count. 5 subs ≈ design's ~180/5-9/25s.
  const estCandidates = Math.max(40, subs.length * 36);
  const estSurfacedLow = Math.max(1, Math.round(estCandidates * 0.028));
  const estSurfacedHigh = Math.max(estSurfacedLow + 1, Math.round(estCandidates * 0.05));
  const estRuntimeSec = Math.max(15, subs.length * 15);

  return (
    <div className="page page-v2 fs-page screen-enter">
      <section className="fs-hero">
        <div className="fs-hero-copy">
          <div className="page-eyebrow">
            <span className="page-eyebrow-dot fs-pulse-dot" />
            Profile ready · No scans yet
          </div>
          <h1 className="page-title fs-title">
            Let's find opportunities for <em style={{letterSpacing: "0.08rem"}}>{profile?.name || "your business"}</em>.
          </h1>
          <p className="fs-subtitle">
            Your profile is ready. Run a scan across your selected {" "}
            <strong>{subs.length} subreddit{subs.length === 1 ? "" : "s"}</strong>{" "}
            and we’ll surface the most relevant conversations to review.
          </p>
     

          <div className="fs-cta-row">
            <button
              className="btn btn-primary btn-lg fs-cta-primary"
              onClick={onScanNow}
            >
              <window.I.Sparkles size={14} />
              Run first scan
            </button>
            <button
              className="btn btn-ghost btn-sm"
              onClick={() => onNavigate("profile")}
            >
              <window.I.Edit size={12} />
              Adjust profile first
            </button>
          </div>

        </div>

        <div className="fs-hero-viz">
          <RadarViz subs={subs} profile={profile} />
        </div>
      </section>

      <section className="fs-brief">
        <div className="fs-section-head">
          <span className="fs-section-num">01</span>
          <span className="fs-section-title">Mission brief</span>
          <span className="fs-section-rule" />
          <span className="fs-section-meta">What this scan will do</span>
        </div>

        <div className="fs-brief-grid">
          <div className="card fs-brief-card">
            <div className="fs-brief-card-head">
              <div className="fs-brief-icon">
                <window.I.Layers size={14} />
              </div>
              <div className="fs-brief-label">Where</div>
              <span className="fs-brief-count">{subs.length}</span>
            </div>
            <div className="fs-brief-card-body">
              <div className="fs-chip-row">
                {subs.map((s) => (
                  <span key={s} className="fs-chip mono">
                    <span
                      className="fs-chip-dot"
                      style={{ background: subColorFs(s) }}
                    />
                    {s}
                  </span>
                ))}
              </div>
            </div>
          </div>

          <div className="card fs-brief-card">
            <div className="fs-brief-card-head">
              <div className="fs-brief-icon">
                <window.I.Sparkles size={14} />
              </div>
              <div className="fs-brief-label">What</div>
              <span className="fs-brief-count">{themes.length}</span>
            </div>
            <div className="fs-brief-card-body">
              <div className="fs-chip-row">
                {themes.map((t) => (
                  <span key={t} className="fs-chip">
                    {t}
                  </span>
                ))}
              </div>
              {keywords.length > 0 && (
                <div className="fs-brief-keywords">
                  <span className="fs-brief-kw-label">keywords</span>
                  {keywords.slice(0, 6).map((k) => (
                    <span key={k} className="fs-brief-kw">
                      {k}
                    </span>
                  ))}
                  {keywords.length > 6 && (
                    <span className="fs-brief-kw fs-brief-kw-more">
                      +{keywords.length - 6}
                    </span>
                  )}
                </div>
              )}
            </div>
          </div>

          <div className="card fs-brief-card">
            <div className="fs-brief-card-head">
              <div className="fs-brief-icon">
                <window.I.User size={14} />
              </div>
              <div className="fs-brief-label">Who</div>
            </div>
            <div className="fs-brief-card-body">
              <div className="fs-brief-customer">{customers}</div>
              {whoBullets.length > 0 && (
                <div className="fs-brief-usecases">
                  {whoBullets.map((u) => (
                    <div key={u} className="fs-brief-usecase">
                      <span className="fs-brief-usecase-dot" />
                      {u}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      <section className="fs-preview">
        <div className="fs-section-head">
          <span className="fs-section-num">02</span>
          <span className="fs-section-title">What you'll see here</span>
          <span className="fs-section-rule" />
          <span className="fs-section-meta">
            Each surfaced thread will look like this
          </span>
        </div>

        <div className="fs-ghost-stack">
          <GhostOpp tier="high" pct={92} sub={subs[0] || "r/subreddit"} ago="3h" />
          <GhostOpp tier="high" pct={87} sub={subs[1] || subs[0] || "r/subreddit"} ago="8h" />
          <GhostOpp tier="medium" pct={74} sub={subs[2] || subs[0] || "r/subreddit"} ago="1d" />
        </div>

        <div className="fs-after-row">
          <div className="fs-after-text">
            After the first scan, this dashboard becomes your daily standup.
            New opportunities, confidence by subreddit, and review activity will be updated here.
          </div>
        </div>
      </section>
    </div>
  );
}

/* ── Radar visualization ─────────────────────────────────────── */
function RadarViz({ subs, profile }) {
  if (!subs.length) return null;
  const subredditMeta = React.useMemo(() => {
    const byName = new Map();
    for (const item of profile?._server?.search_plan?.suggested_subreddits || []) {
      const clean = String(item?.name || "").trim().replace(/^r\//i, "").toLowerCase();
      if (!clean) continue;
      byName.set(clean, {
        communityIcon: item?.community_icon || "",
      });
    }
    for (const item of profile?.subreddits || []) {
      if (!item || typeof item === "string") continue;
      const clean = String(item?.name || item?.subreddit || "").trim().replace(/^r\//i, "").toLowerCase();
      if (!clean) continue;
      byName.set(clean, {
        ...byName.get(clean),
        communityIcon: item?.community_icon || item?.communityIcon || byName.get(clean)?.communityIcon || "",
      });
    }
    return byName;
  }, [profile]);
  const positions = subs.map((s, i) => {
    const angle = (-90 + (360 / subs.length) * i) * (Math.PI / 180);
    return {
      sub: s,
      x: 50 + Math.cos(angle) * 38,
      y: 50 + Math.sin(angle) * 38,
      delay: i * 240,
    };
  });

  return (
    <div className="fs-radar" aria-hidden="true">
      <div className="fs-radar-rings">
        <span className="fs-radar-ring fs-radar-ring-1" />
        <span className="fs-radar-ring fs-radar-ring-2" />
        <span className="fs-radar-ring fs-radar-ring-3" />
        <span className="fs-radar-ring fs-radar-ring-4" />
      </div>

      <div className="fs-radar-sweep" />

      <div className="fs-radar-axis fs-radar-axis-h" />
      <div className="fs-radar-axis fs-radar-axis-v" />

      <div className="fs-radar-core">
        <div
          className="fs-radar-core-mark"
          style={!profile?.faviconUrl ? { background: profile?.color || "var(--accent)" } : undefined}
        >
          {profile?.faviconUrl ? (
            <img className="profile-mark-img" src={profile.faviconUrl} alt="" />
          ) : (
            profile?.initials || "•"
          )}
        </div>
        <div className="fs-radar-core-pulse" />
      </div>

      {positions.map((p) => {
        const icon = subredditMeta.get(p.sub.replace(/^r\//, "").toLowerCase())?.communityIcon || "";
        return (
          <div
            key={p.sub}
            className="fs-radar-sat"
            style={{
              left: `${p.x}%`,
              top: `${p.y}%`,
              animationDelay: `${p.delay}ms`,
            }}
          >
            <span
              className="fs-radar-sat-dot"
              style={icon ? undefined : { background: subColorFs(p.sub) }}
            >
              {icon ? (
                <img className="radar-sat-icon" src={icon} alt="" loading="lazy" />
              ) : (
                p.sub.replace(/^r\//, "").charAt(0).toUpperCase()
              )}
            </span>
            <span className="fs-radar-sat-label mono">{p.sub}</span>
          </div>
        );
      })}

      <div className="fs-radar-telemetry">
        <div className="fs-radar-tel-row">
          <span className="fs-radar-tel-dot" />
          <span className="fs-radar-tel-label">SCANNING IDLE</span>
        </div>
        <div className="fs-radar-tel-row">
          <span className="fs-radar-tel-key">target</span>
          <span className="fs-radar-tel-val mono">{subs.length} subs</span>
        </div>
        <div className="fs-radar-tel-row">
          <span className="fs-radar-tel-key">window</span>
          <span className="fs-radar-tel-val mono">3 d</span>
        </div>
      </div>
    </div>
  );
}

/* ── Ghost opportunity card preview ──────────────────────────── */
function GhostOpp({ tier, pct, sub, ago }) {
  const tierClass =
    tier === "high" ? "pill-success" : tier === "medium" ? "pill-warning" : "pill-neutral";
  return (
    <div className="fs-ghost-card">
      <div className="fs-ghost-head">
        <span className="fs-ghost-sub mono">{sub}</span>
        <span className="opp-meta-sep" />
        <span className="fs-ghost-meta">{ago} ago</span>
        <div className="spacer" />
        <span className="fs-ghost-pct mono">{pct}%</span>
        <span className={"pill " + tierClass + " fs-ghost-tier"}>{tier}</span>
      </div>
      <div className="fs-ghost-title-line fs-ghost-skel" style={{ width: "92%" }} />
      <div className="fs-ghost-title-line fs-ghost-skel" style={{ width: "68%" }} />
      <div className="fs-ghost-why">
        <div className="fs-ghost-skel" style={{ width: "100%", height: 9 }} />
        <div className="fs-ghost-skel" style={{ width: "85%", height: 9 }} />
      </div>
      <div className="fs-ghost-foot">
        <span className="fs-ghost-tag-skel" />
        <span className="fs-ghost-tag-skel" />
        <span className="fs-ghost-tag-skel" style={{ width: 52 }} />
        <div className="spacer" />
        <span className="fs-ghost-btn-skel" />
        <span className="fs-ghost-btn-skel" />
      </div>
    </div>
  );
}

const FS_SUB_PALETTE = [
  "oklch(0.62 0.13 268)",
  "oklch(0.6 0.13 200)",
  "oklch(0.62 0.12 160)",
  "oklch(0.65 0.13 50)",
  "oklch(0.6 0.14 340)",
  "oklch(0.58 0.13 30)",
];
function subColorFs(name) {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) | 0;
  return FS_SUB_PALETTE[Math.abs(h) % FS_SUB_PALETTE.length];
}

window.DashboardFirstScan = DashboardFirstScan;
